import {
  differenceInCalendarDays,
  endOfDay,
  isAfter,
  startOfDay,
  subDays,
} from "date-fns";
import type { DateRange } from "@/hooks/useDateRange";

export function getAnalyticsMaxSelectableDate(): Date {
  return endOfDay(new Date());
}

/** Tabs (value of the `tab` URL param) whose date range may include future dates. */
export const ANALYTICS_FUTURE_DATES_TABS: ReadonlyArray<string> = ["services"];

export function allowsFutureAnalyticsDates(
  tab: string | null | undefined,
): boolean {
  return !!tab && ANALYTICS_FUTURE_DATES_TABS.includes(tab);
}

const SERVICES_ROUTE_PREFIX = "/analytics/services";

/**
 * Tab that decides the date rule. Rapid plan is always on services, the
 * /analytics/services* routes belong to services, the admin dashboard
 * (allowFutureDates=false) never gets future dates.
 */
export function getAnalyticsDateRuleTab({
  searchTab,
  pathname,
  isRapidPlan,
  allowFutureDates = true,
}: {
  searchTab: string | null | undefined;
  pathname?: string | null;
  isRapidPlan?: boolean;
  allowFutureDates?: boolean;
}): string | null {
  if (!allowFutureDates) {
    return null;
  }
  if (isRapidPlan) {
    return "services";
  }
  if (
    pathname &&
    (pathname === SERVICES_ROUTE_PREFIX ||
      pathname.startsWith(`${SERVICES_ROUTE_PREFIX}/`))
  ) {
    return "services";
  }
  return searchTab ?? null;
}

/** Upper bound for the date picker on a tab; undefined means no limit. */
export function getAnalyticsTabMaxDate(
  tab: string | null | undefined,
): Date | undefined {
  return allowsFutureAnalyticsDates(tab)
    ? undefined
    : getAnalyticsMaxSelectableDate();
}

export function isAfterAnalyticsMaxDate(date: Date, maxDate?: Date): boolean {
  const limit = startOfDay(maxDate ?? getAnalyticsMaxSelectableDate());
  return isAfter(startOfDay(date), limit);
}

/** Clamps analytics range ends to today; ensures from <= to when both set. */
export function clampAnalyticsDateRange(
  range: DateRange,
  maxDate: Date = getAnalyticsMaxSelectableDate(),
): DateRange {
  const maxEnd = endOfDay(maxDate);
  let from = range.from;
  let to = range.to;

  if (from && isAfterAnalyticsMaxDate(from, maxDate)) {
    from = maxEnd;
  }
  if (to && isAfterAnalyticsMaxDate(to, maxDate)) {
    to = maxEnd;
  }
  if (from && to && from > to) {
    to = from;
  }

  return { from, to };
}

/** Clamps the range to today unless the tab allows future dates; always keeps from <= to. */
export function clampAnalyticsDateRangeForTab(
  range: DateRange,
  tab: string | null | undefined,
  maxDate: Date = getAnalyticsMaxSelectableDate(),
): DateRange {
  if (!allowsFutureAnalyticsDates(tab)) {
    return clampAnalyticsDateRange(range, maxDate);
  }

  const { from } = range;
  let { to } = range;
  if (from && to && from > to) {
    to = from;
  }
  return { from, to };
}

/** Calendar date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString). */
export function formatLocalDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalDateParam(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }

  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);

  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed;
}

export function toAnalyticsDateParam(
  date: Date | undefined,
): string | undefined {
  if (!date || Number.isNaN(date.getTime())) {
    return undefined;
  }

  return formatLocalDateParam(date);
}

export function getDefaultAnalyticsDateRange(): DateRange {
  const today = new Date();
  return {
    from: startOfDay(subDays(today, 29)),
    to: endOfDay(today),
  };
}

/** Previous period of the same length, ending the day before `dateRange.from`. */
export function getPreviousAnalyticsDateRange(
  dateRange: DateRange,
): DateRange | null {
  if (!dateRange.from || !dateRange.to) {
    return null;
  }

  const inclusiveDays =
    differenceInCalendarDays(dateRange.to, dateRange.from) + 1;
  const previousTo = endOfDay(subDays(startOfDay(dateRange.from), 1));
  const previousFrom = startOfDay(subDays(previousTo, inclusiveDays - 1));

  return { from: previousFrom, to: previousTo };
}
